import { useTranslation } from 'react-i18next';
import { Button, SectionCard } from '@/components/ui';
import type { AppDetailPageVM } from './useAppDetailPage';
import { AppDetailConsiderationsCard } from './AppDetailConsiderationsCard';

/**
 * EAA-1817 (frame 49): Tổng quan tab.
 *
 * The mock's "Phạm vi triển khai" (per-org enable toggles) card is still
 * DELIBERATELY OMITTED: `OrganizationAppScope` has no admin-console view of
 * any kind (not even read — confirmed via `portal_api/urls.py`, only
 * `OrganizationAppScopeService` exists, unwrapped by any HTTP endpoint). Per
 * house convention (see EAA-1819's comment on the missing account-count
 * endpoint): name the gap, ship what has a real source, never fabricate a
 * card around data that does not exist.
 *
 * EAA-1884: the "Việc cần cân nhắc" (Tạm ngưng/Gỡ) card this same section used
 * to omit for the same reason IS now shipped (`AppDetailConsiderationsCard`)
 * — `suspendApp`/`resumeApp`/`retireApp` landed server-side after this comment
 * was written (see `api/console.ts`'s doc comment on `RegistryApp.lifecycle`).
 */
export function AppDetailOverviewTab({ vm }: { vm: AppDetailPageVM }) {
    const { t } = useTranslation();
    const app = vm.app;
    if (!app) return null;

    const owner =
        app.owner_name && app.owner_email
            ? `${app.owner_name} · ${app.owner_email}`
            : app.owner_name || app.owner_email || t('common.noData');

    return (
        <div className="app-detail-grid">
            <div className="app-detail-grid__main">
                <SectionCard title={t('console.appDetail.overview.infoCardTitle')}>
                    <dl className="app-detail-fieldlist">
                        <div className="app-detail-fieldlist__row">
                            <dt>{t('console.appDetail.overview.fieldCode')}</dt>
                            <dd>
                                <code>{app.code}</code>
                            </dd>
                        </div>
                        <div className="app-detail-fieldlist__row">
                            <dt>{t('console.appDetail.overview.fieldName')}</dt>
                            <dd>{app.name}</dd>
                        </div>
                        <div className="app-detail-fieldlist__row">
                            <dt>{t('console.appDetail.overview.fieldWebUrl')}</dt>
                            <dd className="is-link">{app.web_url}</dd>
                        </div>
                        <div className="app-detail-fieldlist__row">
                            <dt>{t('console.appDetail.overview.fieldAudience')}</dt>
                            <dd>
                                <code>{app.audience}</code>
                            </dd>
                        </div>
                        <div className="app-detail-fieldlist__row">
                            <dt>{t('console.appDetail.overview.fieldOwner')}</dt>
                            <dd>{owner}</dd>
                        </div>
                    </dl>
                </SectionCard>
            </div>

            <div className="app-detail-grid__side">
                <SectionCard title={t('console.appDetail.overview.statusCardTitle')}>
                    <div
                        className={
                            'app-detail-status-banner' + (app.enabled ? ' is-approved' : ' is-pending')
                        }
                    >
                        {app.enabled
                            ? t('console.appDetail.overview.statusApprovedBanner')
                            : t('console.appDetail.overview.statusPendingBanner')}
                    </div>
                    <div className="app-detail-metric-list">
                        <div className="app-detail-metric-list__row">
                            <span>{t('console.appDetail.overview.metricRoleCatalog')}</span>
                            <strong>{app.catalog_role_count}</strong>
                        </div>
                        <div className="app-detail-metric-list__row">
                            <span>{t('console.appDetail.overview.metricUnconverted')}</span>
                            <strong className="is-warning">{vm.unconvertedCount}</strong>
                        </div>
                    </div>
                    <p className="app-detail-hint">
                        {app.catalog_last_synced_at
                            ? t('console.appDetail.overview.lastSynced', {
                                  time: new Date(app.catalog_last_synced_at).toLocaleString('vi-VN'),
                              })
                            : t('console.appDetail.overview.neverSynced')}
                    </p>
                </SectionCard>

                <SectionCard title={t('console.appDetail.overview.recentActivityTitle')}>
                    {!vm.canViewAudit && (
                        <p className="portal-admin-hint">{t('console.appDetail.audit.accessDenied')}</p>
                    )}
                    {vm.canViewAudit && vm.auditQuery.isLoading && (
                        <p className="portal-admin-hint">{t('common.loading')}</p>
                    )}
                    {vm.canViewAudit && vm.auditQuery.isSuccess && vm.auditRows.length === 0 && (
                        <p className="portal-admin-hint">
                            {t('console.appDetail.overview.recentActivityEmpty')}
                        </p>
                    )}
                    {vm.canViewAudit && vm.auditRows.length > 0 && (
                        <ul className="app-detail-activity-list">
                            {vm.auditRows.slice(0, 4).map((row) => (
                                <li key={row.id}>
                                    <span className="app-detail-activity-list__time">
                                        {new Date(row.created_at).toLocaleString('vi-VN')}
                                    </span>
                                    <span className="app-detail-activity-list__body">
                                        <strong>{t(`console.appDetail.audit.action.${row.action}`, {
                                            defaultValue: row.action,
                                        })}</strong>
                                        <span>{row.actor_label || t('console.appDetail.audit.systemActor')}</span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {vm.canViewAudit && (
                        <Button
                            type="link"
                            size="small"
                            className="app-detail-link-button"
                            onClick={() => vm.setTab('audit')}
                        >
                            {t('console.appDetail.overview.viewFullAudit')}
                        </Button>
                    )}
                </SectionCard>

                <AppDetailConsiderationsCard app={app} canManage={vm.canManage} />
            </div>
        </div>
    );
}

export default AppDetailOverviewTab;
